import { LoaderIcon } from 'lucide-react'
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

const MAX_PULL_DISTANCE = 100

interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => void
}

const PullToRefresh = ({ children, onRefresh }: PullToRefreshProps) => {
  const spinnerRef = useRef<HTMLDivElement>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [startY, setStartY] = useState(0)
  const [isTouch, setIsTouch] = useState(false)
  const [pulled, setPulled] = useState(false)

  const resetToInitial = useCallback(() => {
    if (spinnerRef.current) {
      spinnerRef.current.style.height = '0'
      spinnerRef.current.style.willChange = 'unset'
    }
    setPulled(false)
    setIsRefreshing(false)
  }, [])

  const onMove = useCallback(
    (y: number) => {
      if (pulled && spinnerRef.current) {
        const moveY = y
        const pulledDistance = Math.min(Math.pow(moveY - startY, 0.875), MAX_PULL_DISTANCE)

        if (pulledDistance > 0) {
          spinnerRef.current.style.height = `${pulledDistance}px`
          document.body.style.overflow = 'hidden'

          if (pulledDistance >= MAX_PULL_DISTANCE) {
            setIsRefreshing(true)
          } else {
            setIsRefreshing(false)
          }
        } else {
          document.body.style.overflow = 'auto'
          resetToInitial()
        }
      }
    },
    [pulled, startY, resetToInitial]
  )

  const onStart = useCallback((y: number, touch: boolean) => {
    setStartY(y)
    setIsTouch(touch)
    setPulled(true)
    if (spinnerRef.current) {
      spinnerRef.current.style.willChange = 'height'
    }
  }, [])

  const handleEnd = useCallback(async () => {
    if (isTouch && pulled) {
      document.body.style.overflow = 'auto'
      if (isRefreshing) {
        try {
          await onRefresh()
          await new Promise(resolve => setTimeout(resolve, 500))
          resetToInitial()
        } catch (error) {
          console.error('Error while refreshing:', error)
          resetToInitial()
        }
      } else {
        resetToInitial()
      }
    }
  }, [isTouch, pulled, isRefreshing, onRefresh, resetToInitial])

  useEffect(() => {
    const touchMoveListener = (e: TouchEvent) => {
      if (isTouch && pulled) {
        const moveY = e.touches[0].clientY
        const pulledDistance = Math.min(Math.pow(moveY - startY, 0.875), MAX_PULL_DISTANCE)

        // 페이지 최상단이고 아래로 당기는 경우에만 preventDefault
        if (window.scrollY === 0 && pulledDistance > 0) {
          e.preventDefault()
          onMove(moveY)
        }
      }
    }

    if (isTouch && pulled) {
      document.addEventListener('touchmove', touchMoveListener, { passive: false })
    }

    return () => {
      document.removeEventListener('touchmove', touchMoveListener)
    }
  }, [isTouch, pulled, onMove, startY])

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (window.scrollY === 0) {
        onStart(e.touches[0].clientY, true)
      }
    },
    [onStart]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (window.scrollY === 0) {
        onStart(e.clientY, false)
      }
    },
    [onStart]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isTouch && pulled) {
        onMove(e.clientY)
        e.preventDefault()
      }
    },
    [isTouch, pulled, onMove]
  )

  const handleMouseUp = useCallback(() => {
    if (!isTouch) {
      handleEnd()
    }
  }, [isTouch, handleEnd])

  return (
    <div>
      <div ref={spinnerRef}>{isRefreshing && <LoaderIcon className='w-8 h-8 animate-spin mx-auto' />}</div>
      <div
        onTouchStart={handleTouchStart}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleEnd}
        style={{ cursor: 'pointer' }}
      >
        {children}
      </div>
    </div>
  )
}

export default PullToRefresh
